import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { verifyMentorReflectionAccess } from "@/lib/security-utils";
import {
	getErrorMessage,
	getUserFriendlyErrorMessage,
} from "@/utils/error-utils";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Badge,
	Input,
	Label,
	Switch,
} from "@/components/primitives";
import { formatDate } from "@/lib/utils";
import {
	ArrowLeft,
	Calendar,
	Target,
	Lightbulb,
	CheckCircle,
	AlertCircle,
	FileText,
	Activity,
	Save,
	User,
	Edit3,
} from "lucide-react";

interface Reflection {
	id: string;
	title: string;
	description: string;
	current_state: string | null;
	future_state: string | null;
	qa_results: any;
	topic_tags: string[];
	created_at: string;
	updated_at: string;
	is_draft?: boolean;
	user_id: string;
	profiles: {
		name: string;
		email: string;
	};
}

interface ContributingFactor {
	id: string;
	reflection_id: string;
	title: string;
	description: string;
	created_at: string;
}

interface Signal {
	id: string;
	reflection_id: string;
	title: string;
	description: string;
	created_at: string;
}

interface CorrectiveAction {
	id: string;
	reflection_id: string;
	title: string;
	description: string;
	priority: number;
	status: "not_started" | "in_progress" | "completed";
	created_at: string;
	updated_at: string;
}

interface MentorNote {
	id: string;
	mentor_id: string;
	reflection_id: string;
	notes: string;
	reviewed?: boolean;
	created_at: string;
	updated_at: string;
}

export function MentorReflectionDetail() {
	const { reflectionId } = useParams<{ reflectionId: string }>();
	const navigate = useNavigate();
	const { user, profile } = useAuth();
	const [reflection, setReflection] = useState<Reflection | null>(null);
	const [contributingFactors, setContributingFactors] = useState<
		ContributingFactor[]
	>([]);
	const [signals, setSignals] = useState<Signal[]>([]);
	const [correctiveActions, setCorrectiveActions] = useState<
		CorrectiveAction[]
	>([]);
	const [mentorNote, setMentorNote] = useState<MentorNote | null>(null);
	const [noteText, setNoteText] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (reflectionId && profile?.user_type === "mentor") {
			fetchReflectionData();
		}
	}, [reflectionId, profile]);

	const fetchReflectionData = async () => {
		try {
			setLoading(true);
			setError(null);

			if (!reflectionId || !user?.id) {
				setError("Reflection ID and user authentication are required");
				return;
			}

			// Verify mentor has access to this reflection before fetching
			const hasAccess = await verifyMentorReflectionAccess(
				user.id,
				reflectionId,
			);
			if (!hasAccess) {
				setError(
					"Access denied. You do not have permission to view this reflection.",
				);
				setLoading(false);
				return;
			}

			// Fetch reflection and related data directly from the public tables.
			// Mentor-specific data (mentor_notes) is stored in a mentor table so mentors don't need to write to mentee rows.
			const [
				{ data: reflectionData, error: reflectionError },
				{ data: factorsData },
				{ data: signalsData },
				{ data: correctiveData },
				{ data: mentorNoteData },
			] = await Promise.all([
				supabase
					.from("reflections")
					.select(`*, profiles!reflections_user_id_fkey(name, email)`)
					.eq("id", reflectionId)
					.maybeSingle(),
				supabase
					.from("contributing_factors")
					.select("*")
					.eq("reflection_id", reflectionId),
				supabase.from("signals").select("*").eq("reflection_id", reflectionId),
				supabase
					.from("corrective_actions")
					.select("*")
					.eq("reflection_id", reflectionId),
				// Mentor-specific note (only for the current mentor)
				supabase
					.from("mentor_notes")
					.select("*")
					.eq("reflection_id", reflectionId)
					.eq("mentor_id", user?.id)
					.maybeSingle(),
			]);

			if (reflectionError) {
				console.error("Error fetching reflection:", reflectionError);
				const errorMsg = getUserFriendlyErrorMessage(
					reflectionError,
					"fetching reflection",
				);
				setError(errorMsg);
				return;
			}

			if (!reflectionData) {
				setError("Reflection not found");
				return;
			}

			setReflection(reflectionData as any);
			setContributingFactors(factorsData || []);
			setSignals(signalsData || []);
			setCorrectiveActions(correctiveData || []);

			if (mentorNoteData) {
				setMentorNote(mentorNoteData as any);
				setNoteText((mentorNoteData as any).notes || "");
			}
		} catch (error) {
			console.error("Error fetching reflection data:", error);
			const errorMsg = getUserFriendlyErrorMessage(
				error,
				"loading reflection data",
			);
			setError(errorMsg);
		} finally {
			setLoading(false);
		}
	};

	const saveMentorNote = async () => {
		if (!reflectionId || !user?.id) return;

		try {
			setSaving(true);
			const { data, error } = await supabase
				.from("mentor_notes")
				.upsert(
					{
						mentor_id: user.id,
						reflection_id: reflectionId,
						notes: noteText,
						reviewed: mentorNote?.reviewed || false,
					},
					{ onConflict: "mentor_id, reflection_id" },
				)
				.select()
				.single();

			setMentorNote(data);

			if (error) {
				throw error;
			}
		} catch (error) {
			console.error("Error saving mentor note:", error);
			alert("Failed to save note. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	const getStatusBadgeColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800 border-green-200";
			case "in_progress":
				return "bg-yellow-100 text-yellow-800 border-yellow-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="h-3 w-3" />;
			case "in_progress":
				return <Activity className="h-3 w-3" />;
			default:
				return <AlertCircle className="h-3 w-3" />;
		}
	};

	const getPriorityBadge = (priority: number) => {
		if (priority >= 3) {
			return (
				<Badge className="bg-red-100 text-red-800 border-red-200">High</Badge>
			);
		} else if (priority >= 2) {
			return (
				<Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
					Medium
				</Badge>
			);
		} else {
			return (
				<Badge className="bg-gray-100 text-gray-800 border-gray-200">Low</Badge>
			);
		}
	};

	if (profile?.user_type !== "mentor") {
		return (
			<div className="container mx-auto px-4 py-8">
				<Card className="max-w-md mx-auto">
					<CardContent className="text-center py-8">
						<p className="text-muted-foreground">
							Access denied. Mentor privileges required.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
				</div>
			</div>
		);
	}

	if (error || !reflection) {
		return (
			<div className="container mx-auto px-4 py-8 max-w-7xl">
				<div className="text-center py-8">
					<AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						{error || "Reflection not found"}
					</h2>
					<p className="text-gray-600 mb-4">
						The reflection you're looking for doesn't exist or you don't have
						permission to view it.
					</p>
					<Button
						onClick={() =>
							window.history.length > 1 ? navigate(-1) : navigate("/mentor")
						}
						variant="outline"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Dashboard
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() =>
							window.history.length > 1 ? navigate(-1) : navigate("/mentor")
						}
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Dashboard
					</Button>
					<div className="h-6 w-px bg-gray-300" />
					<div className="flex items-center space-x-4">
						<div className="flex items-center space-x-2">
							<Badge
								variant="outline"
								className="text-blue-600 border-blue-200"
							>
								Mentor View
							</Badge>
							{reflection.is_draft && (
								<Badge
									variant="outline"
									className="text-gray-600 border-gray-200"
								>
									Draft
								</Badge>
							)}
							{reflection.topic_tags?.map((tag) => (
								<Badge key={tag} variant="secondary">
									{tag}
								</Badge>
							))}
						</div>

						<div className="flex items-center space-x-2">
							<span className="text-sm text-muted-foreground">
								Completed Review
							</span>
							<Switch
								checked={!!mentorNote?.reviewed}
								onCheckedChange={async (val) => {
									try {
										// toggle reviewed state
										if (!reflectionId || !user?.id) return;
										if (mentorNote) {
											const { data, error } = await supabase
												.from("mentor_notes")
												.upsert({
													id: mentorNote.id,
													mentor_id: user.id,
													reflection_id: reflectionId,
													notes: mentorNote.notes || "",
													reviewed: val,
												})
												.select()
												.single();
											if (error) throw error;
											setMentorNote(data);
										} else {
											const { data, error } = await supabase
												.from("mentor_notes")
												.upsert(
													{
														mentor_id: user.id,
														reflection_id: reflectionId,
														notes: noteText || "",
														reviewed: val,
													},
													{ onConflict: "reflection_id, mentor_id" },
												)
												.select()
												.single();
											if (error) throw error;
											setMentorNote(data);
										}
									} catch (err) {
										console.error("Error updating review status", err);
										alert("Failed to update review status");
									}
								}}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Mentor Notes Section */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg font-medium flex items-center">
						<Edit3 className="h-5 w-5 mr-2" />
						Mentor Notes
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<Label htmlFor="mentorNotes">
								Your notes about this reflection
							</Label>
							<textarea
								id="mentorNotes"
								value={noteText}
								onChange={(e) => setNoteText(e.target.value)}
								placeholder="Add your professional observations, insights, or recommendations about this mentee's reflection..."
								className="w-full min-h-[120px] px-3 py-2 border border-input bg-background rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
							/>
						</div>
						<div className="flex items-center justify-between">
							<div className="text-sm text-gray-500">
								{mentorNote ? (
									<>Last updated: {formatDate(mentorNote.updated_at)}</>
								) : (
									<>No notes yet</>
								)}
							</div>
							<Button
								onClick={saveMentorNote}
								disabled={saving || !noteText.trim()}
							>
								<Save className="h-4 w-4 mr-2" />
								{saving ? "Saving..." : "Save Notes"}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Title and Basic Info */}
			<Card>
				<CardHeader>
					<div className="flex items-start justify-between">
						<div className="flex-1">
							<CardTitle className="text-2xl font-medium mb-3">
								{reflection.title}
							</CardTitle>
							<div className="flex items-center space-x-6 text-sm text-gray-500">
								<div className="flex items-center">
									<User className="h-4 w-4 mr-2" />
									<span className="font-medium">
										{reflection.profiles.name}
									</span>
									<span className="ml-2 text-gray-400">
										({reflection.profiles.email})
									</span>
								</div>
								<div className="flex items-center">
									<Calendar className="h-4 w-4 mr-1" />
									Created {formatDate(reflection.created_at)}
								</div>
								{reflection.updated_at !== reflection.created_at && (
									<div className="flex items-center">
										<Edit3 className="h-4 w-4 mr-1" />
										Updated {formatDate(reflection.updated_at)}
									</div>
								)}
							</div>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<h4 className="font-medium mb-3 flex items-center">
								<FileText className="h-4 w-4 mr-2" />
								Reflection Summary
							</h4>
							<div className="border rounded-lg p-4">
								<p className="text-gray-700 leading-relaxed">
									{reflection.description}
								</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Current and Future State */}
			{(reflection.current_state || reflection.future_state) && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{reflection.current_state && (
						<Card>
							<CardHeader>
								<CardTitle className="text-lg font-medium flex items-center">
									<AlertCircle className="h-5 w-5 mr-2 text-red-500" />
									Current State
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="border rounded-lg p-4">
									<p className="text-gray-700 leading-relaxed">
										{reflection.current_state}
									</p>
								</div>
							</CardContent>
						</Card>
					)}
					{reflection.future_state && (
						<Card>
							<CardHeader>
								<CardTitle className="text-lg font-medium flex items-center">
									<Target className="h-5 w-5 mr-2 text-green-500" />
									Future State
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="border rounded-lg p-4">
									<p className="text-gray-700 leading-relaxed">
										{reflection.future_state}
									</p>
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			)}

			{/* Q&A History */}
			{reflection.qa_results &&
				Array.isArray(reflection.qa_results) &&
				reflection.qa_results.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg font-medium flex items-center">
								<Activity className="h-5 w-5 mr-2" />
								Exploration Questions & Answers
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								{reflection.qa_results.map((qa: any, index: number) => (
									<div
										key={index}
										className="border-l-4 border-blue-200 pl-4 py-3 rounded-r-lg"
									>
										<h5 className="font-medium text-gray-900 mb-2">
											Q: {qa.question}
										</h5>
										<p className="text-gray-700">{qa.answer}</p>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				)}

			{/* Chosen Signals */}
			{signals.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-medium flex items-center">
							<Activity className="h-5 w-5 mr-2" />
							Chosen Signals ({signals.length})
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{signals.map((signal) => (
								<div key={signal.id} className="border rounded-lg p-4">
									<h4 className="font-medium text-gray-900 mb-2">
										{signal.title}
									</h4>
									<p className="text-sm text-gray-600">{signal.description}</p>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Chosen Contributing Factors */}
			{contributingFactors.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-medium flex items-center">
							<Lightbulb className="h-5 w-5 mr-2" />
							Chosen Contributing Factors ({contributingFactors.length})
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{contributingFactors.map((factor) => (
								<div key={factor.id} className="border rounded-lg p-4">
									<h4 className="font-medium text-gray-900 mb-2">
										{factor.title}
									</h4>
									<p className="text-sm text-gray-600">{factor.description}</p>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Chosen Corrective Actions */}
			{correctiveActions.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-medium flex items-center">
							<CheckCircle className="h-5 w-5 mr-2" />
							Chosen Corrective Actions ({correctiveActions.length})
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{correctiveActions.map((action) => (
								<div key={action.id} className="border rounded-lg p-4">
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<div className="flex items-center space-x-3 mb-2">
												<h4 className="font-medium text-gray-900">
													{action.title}
												</h4>
												<Badge
													variant="outline"
													className={getStatusBadgeColor(action.status)}
												>
													<span className="flex items-center space-x-1">
														{getStatusIcon(action.status)}
														<span className="capitalize">
															{action.status.replace("_", " ")}
														</span>
													</span>
												</Badge>
												{getPriorityBadge(action.priority)}
											</div>
											<p className="text-sm text-gray-600">
												{action.description}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Empty States */}
			{contributingFactors.length === 0 &&
				signals.length === 0 &&
				correctiveActions.length === 0 && (
					<Card>
						<CardContent className="text-center py-8">
							<Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								Reflection in Progress
							</h3>
							<p className="text-gray-600">
								This reflection doesn't have detailed analysis yet. The mentee
								may still be working on it.
							</p>
						</CardContent>
					</Card>
				)}
		</div>
	);
}
